import React, { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useOpsContext } from "../../../ContextsAndProvider/OpsContext";
import OpsOrganizationPractitionerSelector from "./OpsOrganizationPractitionerSelector";
import { InfoOutlined } from "@mui/icons-material";
import { Table } from "react-bootstrap";
import { Skeleton } from "@mui/material";
import { Button } from "../../common/Button";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import { searchEncounters, getEncounterRecord } from "../../apps/api/AppointmentRequests";
import { failed } from "../../common/Toastify";
import CustomPagination from "../../common/components/Pagination/CustomPagination";
import { ViewDateTime } from "../../../utils/DateSupport";
import moment from "moment";
import {
  statusOptions as statusOptionsRaw,
  encounterOptions,
  encounterTableTitle,
  noDataAvailText,
  isOpenDisclaimer,
} from "../../apps/Encounters/constant";
import "./OpsDashboard.css";

const rowsPerPage = 8;
const defaultStart = moment().subtract(2, "days").format("YYYY-MM-DD");
const defaultEnd = moment().format("YYYY-MM-DD");

const OpsDashboardEncounter = () => {
  const navigate = useNavigate();
  const { selectedOrganization, selectedPractitioner, organizations } = useOpsContext();

  const [showDashboard, setShowDashboard] = useState(false);
  const [encounterData, setEncounterData] = useState([]);
  const [encounterCount, setEncounterCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => {
    setShowDashboard(!!selectedOrganization);
  }, [selectedOrganization]);

  useEffect(() => {
    formik.setFieldValue("practitionerId", selectedPractitioner?.value ?? "");
    if (!selectedOrganization) {
      setEncounterData([]);
      setEncounterCount(0);
      setPage(0);
      return;
    }
    setPage(0);
    runSearch(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization, selectedPractitioner]);

  const selectedOrgName = useMemo(
    () => organizations?.find((o) => o.id === selectedOrganization)?.name,
    [organizations, selectedOrganization]
  );

  const capitalizeWords = (str) =>
    str ? str.toLowerCase().replace(/(^|\s)\S/g, (l) => l.toUpperCase()) : "";

  const sortByDate = (encounters, type) => {
    if (!encounters || !Array.isArray(encounters)) return [];
    return [...encounters].sort((a, b) => {
      const startA = new Date(a?.period?.start);
      const startB = new Date(b?.period?.start);
      const endA = new Date(a?.period?.end);
      const endB = new Date(b?.period?.end);
      if (type === "open") return startB - startA;
      return endB - endA;
    });
  };

  const formik = useFormik({
    initialValues: {
      id: "",
      status: "",
      practitionerId: "",
      start: defaultStart,
      end: defaultEnd,
      isOpen: "closed",
    },
    onSubmit: (values) => {
      if (!selectedOrganization) return;
      setIsLoading(true);

      const trimmedId = values.id?.trim();

      if (trimmedId) {
        getEncounterRecord({ encounterId: trimmedId })
          .then((res) => {
            setEncounterData(res?.data ? [res.data] : []);
            setEncounterCount(res?.data ? 1 : 0);
          })
          .catch(() => {
            failed("No Encounter Found");
            setEncounterData([]);
            setEncounterCount(0);
          })
          .finally(() => setIsLoading(false));
        return;
      }

      const startDate = moment(values.start).startOf("day").valueOf();
      const endDate = moment(values.end).endOf("day").valueOf();

      searchEncounters({
        orgId: selectedOrganization,
        start: startDate,
        end: endDate,
        type: values.isOpen,
        encounterId: null,
        status: values.status || null,
        practitionerId: selectedPractitioner?.value ?? null,
        page: page + 1,
        pageSize: rowsPerPage,
        getAll: false,
      })
        .then((res) => {
          setEncounterData(sortByDate(res?.data || [], values.isOpen));
          setEncounterCount(res?.count?.Count || 0);
        })
        .catch((err) => {
          failed(err?.response?.data?.message || err?.response?.data?.error || err.message);
          setEncounterData([]);
          setEncounterCount(0);
        })
        .finally(() => setIsLoading(false));
    },
  });

  const runSearch = (pageNum = 0) => {
    if (!selectedOrganization) return;

    const values = formik.values;
    const trimmedId = values.id?.trim();
    if (trimmedId) return;

    setIsLoading(true);

    const startDate = moment(values.start).startOf("day").valueOf();
    const endDate = moment(values.end).endOf("day").valueOf();

    searchEncounters({
      orgId: selectedOrganization,
      start: startDate,
      end: endDate,
      type: values.isOpen,
      status: values.status || null,
      practitionerId: selectedPractitioner?.value ?? null,
      page: pageNum + 1,
      pageSize: rowsPerPage,
      getAll: false,
    })
      .then((res) => {
        setEncounterData(sortByDate(res?.data || [], values.isOpen));
        setEncounterCount(res?.count?.Count || 0);
      })
      .catch((err) => {
        failed(err?.response?.data?.message || err?.response?.data?.error || err.message);
        setEncounterData([]);
        setEncounterCount(0);
      })
      .finally(() => setIsLoading(false));
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    runSearch(newPage);
  };

  const handleFormSubmit = (e) => {
    e?.preventDefault();
    setPage(0);
    formik.handleSubmit();
  };

  const handleReset = () => {
    formik.resetForm({
      values: {
        id: "",
        status: "",
        practitionerId: "",
        start: defaultStart,
        end: defaultEnd,
        isOpen: "closed",
      },
    });
    setEncounterData([]);
    setEncounterCount(0);
    setPage(0);
  };

  const handleViewPatient = (patientId) => {
    const url = `${window.location.origin}/app/patients-details?patientId=${patientId}`;
    window.open(url, "_blank");
  };

  const handleViewEncounter = (encounterId, status) => {
    if (status === "cancelled") {
      failed("Cancelled encounters do not have Encounter Reports");
      return;
    }
    navigate("/app/encounter-details", { state: { encounterId, notPatientDashboard: true } });
  };

  return (
    <div className="ops-dashboard-page-wrap">
      <section className="common-listing ops-dashboard-page">
        <div className="heading-wrap ops-dashboard-heading">
          <h1>Ops Dashboard - Encounter Search</h1>
        </div>

        <OpsOrganizationPractitionerSelector />

        {showDashboard && (
          <>
            <div className="ops-read-only-banner">
              <InfoOutlined />
              <span>
                Read-only view - Organization: {selectedOrgName || "-"}
                {selectedPractitioner?.label
                  ? ` | Practitioner: ${selectedPractitioner.label}`
                  : ""}
              </span>
            </div>

            <div className="ops-dashboard-content">
              <form className="ops-encounter-form" onSubmit={handleFormSubmit}>
                <div className="ops-encounter-bar">
                  <div className="ops-encounter-filters-row">
                    <div className="ops-encounter-filters">
                      <div className="ops-field">
                        <label className="ops-field__label">Encounter Id</label>
                        <input
                          type="text"
                          className="ops-field__control"
                          placeholder="Encounter Id"
                          value={formik.values.id}
                          onChange={(e) => formik.setFieldValue("id", e.target.value)}
                        />
                      </div>

                      {formik.values.id.trim() === "" && (
                        <>
                          <div className="ops-field">
                            <label className="ops-field__label">Status</label>
                            <select
                              className="ops-field__control"
                              value={formik.values.status}
                              onChange={(e) => formik.setFieldValue("status", e.target.value)}
                            >
                              <option value="">Select</option>
                              {statusOptionsRaw.map((o) => (
                                <option key={o.value} value={o.value}>{o.name}</option>
                              ))}
                            </select>
                          </div>

                          <div className="ops-field">
                            <label className="ops-field__label">Start Date</label>
                            <input
                              type="date"
                              className="ops-field__control"
                              value={formik.values.start}
                              onChange={(e) => formik.setFieldValue("start", e.target.value)}
                              max={formik.values.end}
                            />
                          </div>

                          <div className="ops-field">
                            <label className="ops-field__label">End Date</label>
                            <input
                              type="date"
                              className="ops-field__control"
                              value={formik.values.end}
                              onChange={(e) => formik.setFieldValue("end", e.target.value)}
                              min={formik.values.start}
                            />
                          </div>

                          <div className="ops-field">
                            <label className="ops-field__label" title={isOpenDisclaimer}>
                              Sort by date
                              <span className="ops-field__info" title={isOpenDisclaimer}><InfoOutlined /></span>
                            </label>
                            <select
                              className="ops-field__control"
                              value={formik.values.isOpen}
                              onChange={(e) => formik.setFieldValue("isOpen", e.target.value)}
                            >
                              {encounterOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.name}</option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="ops-encounter-actions">
                    <Button type="submit" isLoading={isLoading}>
                      <SearchOutlinedIcon /> Search
                    </Button>
                    <Button type="button" onClick={handleReset} variant="secondary" title="Clear">
                      <ClearIcon /> Clear
                    </Button>
                  </div>
                </div>
              </form>

              <div className="table-wrap ops-encounter-table-wrap">
                <Table responsive>
                  <thead>
                    <tr>
                      {encounterTableTitle?.map((title) => (
                        <th key={title}>{title}</th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {isLoading ? (
                      Array.from({ length: rowsPerPage }).map((_, idx) => (
                        <tr key={idx}>
                          {encounterTableTitle?.map((__, i) => (
                            <td key={i}>
                              <Skeleton animation="wave" />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : encounterData?.length > 0 ? (
                      encounterData.map(({ id, period, participant, subject, status }) => {
                        const patientId = subject?.reference?.split("/")[1] || "";
                        const patientName = subject?.display || null;

                        const seen = new Set();
                        const practitioners = (participant ?? []).filter((p) => {
                          if (!p?.individual || p.individual.type !== "Practitioner") return false;
                          const rawName = p.individual.display?.trim();
                          if (!rawName) return false;
                          const key = rawName.toLowerCase();
                          if (seen.has(key)) return false;
                          seen.add(key);
                          return true;
                        });

                        const practitionersNameList = practitioners
                          .map((p) => capitalizeWords(p.individual.display))
                          .join(", ");

                        return (
                          <tr key={id}>
                            <td>
                              <span className="ops-encounter-link" onClick={() => handleViewEncounter(id, status)}>
                                {id}
                              </span>
                            </td>
                            <td>
                              {patientName ? (
                                <span className="ops-encounter-link" onClick={() => handleViewPatient(patientId)}>
                                  {patientName}
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>{practitionersNameList || "-"}</td>
                            <td>{period?.start ? ViewDateTime(period.start) : "-"}</td>
                            <td>{period?.end ? ViewDateTime(period.end) : "-"}</td>
                            <td>{status || "-"}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={encounterTableTitle?.length || 6} style={{ textAlign: "center" }}>
                          {noDataAvailText}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {encounterData?.length > 0 && encounterCount > rowsPerPage && (
                <CustomPagination
                  tableData={encounterData}
                  totalRows={encounterCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  handlePage={handlePageChange}
                />
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default OpsDashboardEncounter;
